import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { ProjectId } from 'api/models';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { RTE } from 'components/atoms/rte';
import { useCurrentProjectId } from 'contexts';
import { tryEmojify } from 'utils/emojify';
import { queueMacrotask } from 'utils/macrotask';
import { CommentRichTextActionBar } from './action-bar';
import { CommentRichTextActionBtns } from './action-btns';

const Wrapper = styled.div`
  position: relative;

  &:focus-within {
    box-shadow: 0 0 0 1px var(--blue-60);
  }

  .ck-placeholder {
    height: 0;
  }
`;

const StyledRTE = styled(RTE)`
  --rte-max-height: auto;
  --rte-content-padding: var(--spacing-02) 20% var(--spacing-02) 0;
  --rte-min-height: auto;
  --rte-readonly-content-padding: var(--spacing-01) 20% var(--spacing-01) 0;
  --rte-transition: none;

  ${({ readonly }) =>
    !readonly &&
    css`
      --rte-max-height: 30vh;
      --rte-action-bar-height: auto;
      --rte-content-padding: 0 var(--spacing-01);

      margin: var(--spacing-01) 0 0;
    `};
`;

export const UserMessageRichText: FC<{
  canEditComment: boolean;
  content: string;
  isPrivate: boolean;
  saveComment(content: string): void;
  cancelChanges(): void;
  disallowMentions?: boolean;
  hasReply?: boolean;
}> = ({
  canEditComment,
  content,
  isPrivate,
  saveComment,
  cancelChanges,
  disallowMentions = false,
}) => {
  const [rteContent, setContent] = useState(content);
  const [rte, setRte] = useState<RichTextEditor | null>(null);
  const intl = useIntl();
  const placeholderText = intl.formatMessage(
    {
      id: 'conversations.placeholders.send_a_message',
    },
    {
      private: !!isPrivate,
    },
  );

  useEffect(() => {
    queueMacrotask(() => rte?.focus());
  }, [canEditComment, rte]);

  useEffect(() => {
    setContent(content);
  }, [content]);

  const onCancel = () => {
    setContent(content);
    cancelChanges();
  };

  const onSave = () => {
    saveComment(rteContent);
  };

  const onChangeRTEContent = (latest) => setContent(latest);
  const projectId = useCurrentProjectId();

  if (!canEditComment) {
    // eslint-disable-next-line react/no-danger, @typescript-eslint/naming-convention
    return <div dangerouslySetInnerHTML={{ __html: tryEmojify(content) }} />;
  }

  return (
    <>
      <Wrapper>
        <StyledRTE
          projectId={ProjectId(projectId)}
          disallowMentions={disallowMentions}
          ref={(el) => setRte(el)}
          data={rteContent}
          placeholder={placeholderText}
          disallowImages
          readonly={!canEditComment}
          onChange={onChangeRTEContent}
          onEnter={onSave}
          noTableFormatting
        />
        {canEditComment && (
          <CommentRichTextActionBar isPrivate={isPrivate} rte={rte} />
        )}
      </Wrapper>
      {canEditComment && (
        <CommentRichTextActionBtns
          isSaveDisabled={!content}
          saveComment={onSave}
          cancelChanges={onCancel}
        />
      )}
    </>
  );
};
